import React, { useEffect, useRef, useState, useCallback } from 'react';
import Send from 'lucide-react/dist/esm/icons/send';
import Paperclip from 'lucide-react/dist/esm/icons/paperclip';
import Sparkles from 'lucide-react/dist/esm/icons/sparkles';
import ArrowLeft from 'lucide-react/dist/esm/icons/arrow-left';
import User from 'lucide-react/dist/esm/icons/user';
import Info from 'lucide-react/dist/esm/icons/info';
import UserPlus from 'lucide-react/dist/esm/icons/user-plus';
import Mail from 'lucide-react/dist/esm/icons/mail';
import FileText from 'lucide-react/dist/esm/icons/file-text';
import Bell from 'lucide-react/dist/esm/icons/bell';
import BellOff from 'lucide-react/dist/esm/icons/bell-off';
import StopCircle from 'lucide-react/dist/esm/icons/stop-circle';
import CheckCircle from 'lucide-react/dist/esm/icons/check-circle';
import X from 'lucide-react/dist/esm/icons/x';
import { useDiscoveryStore } from '../../stores/discoveryStore';
import { streamChat } from '../../services/api/discovery';
import { discoveryApi } from '../../services/api/discovery';
import { useToast } from '../../components/ui';
import { parseMarkdown, formatMessageTime } from '../../utils/markdown';
import Image from 'lucide-react/dist/esm/icons/image';
import Film from 'lucide-react/dist/esm/icons/film';
import Volume2 from 'lucide-react/dist/esm/icons/volume-2';
import File from 'lucide-react/dist/esm/icons/file';
import Search from 'lucide-react/dist/esm/icons/search';
import Globe from 'lucide-react/dist/esm/icons/globe';
import type { DiscoveryMessage, AgentAction, DiscoveryStatus, Attachment, UserLookupResult } from './types';
import StakeholderPanel from './StakeholderPanel';
import { LANGUAGE_OPTIONS } from './constants';

const STATUS_BADGE: Record<DiscoveryStatus, { bg: string; text: string; label: string }> = {
  draft: { bg: 'bg-gray-500/15', text: 'text-gray-400', label: 'Draft' },
  collecting: { bg: 'bg-blue-500/15', text: 'text-blue-400', label: 'Collecting' },
  spec_ready: { bg: 'bg-green-500/15', text: 'text-green-400', label: 'Spec Ready' },
  submitted: { bg: 'bg-purple-500/15', text: 'text-purple-400', label: 'Submitted' },
  in_progress: { bg: 'bg-amber-500/15', text: 'text-amber-400', label: 'In Progress' },
  implemented: { bg: 'bg-emerald-500/15', text: 'text-emerald-400', label: 'Implemented' },
  declined: { bg: 'bg-status-error/15', text: 'text-status-error', label: 'Declined' },
};

const ROLE_CONFIG = {
  user: { icon: User, label: 'You', color: 'bg-brand/15 text-primary' },
  agent: { icon: Sparkles, label: 'Discovery Agent', color: 'bg-purple-500/15 text-purple-400' },
  system: { icon: Info, label: 'System', color: 'bg-surface-secondary text-content-tertiary' },
} as const;

const DiscoveryChat: React.FC = () => {
  const {
    activeRequest,
    isStreaming,
    streamingText,
    pendingActions,
    setView,
    setStreaming,
    appendStreamToken,
    setStreamingText,
    setPendingActions,
    addMessage,
    addStakeholder,
    updateInitiatorConversationStatus,
    loadRequest,
    setError,
  } = useDiscoveryStore();

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const autoTriggeredRef = useRef<string | null>(null);

  const [input, setInput] = useState('');
  const [emailNotify, setEmailNotify] = useState(false);
  const [showPanel, setShowPanel] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [paused, setPaused] = useState(() => {
    const mySh = activeRequest?.stakeholders.find((s) => s.role === 'initiator');
    return mySh?.conversation_status === 'paused';
  });
  const [addingStakeholder, setAddingStakeholder] = useState(false);
  const [generatingSpec, setGeneratingSpec] = useState(false);

  const { error: toastError } = useToast();
  const requestId = activeRequest?.id;
  const messages = activeRequest?.messages || [];

  const sendToAgent = useCallback(
    (content: string, addLocalUserMsg: boolean) => {
      if (!requestId || isStreaming) return;

      if (addLocalUserMsg) {
        addMessage({
          id: `temp-${Date.now()}`,
          request_id: requestId,
          stakeholder_id: null,
          role: 'user',
          content,
          metadata_json: null,
          channel: 'web',
          target_stakeholder_id: null,
          sender_name: 'You',
          sender_email: null,
          created_at: new Date().toISOString(),
        });
      }

      setStreaming(true);
      setStreamingText('');
      setPendingActions([]);

      let fullText = '';
      abortRef.current = streamChat(
        `/api/discovery/requests/${requestId}/chat`,
        content,
        (token) => { fullText += token; appendStreamToken(token); },
        (actions) => {
          if (actions) {
            setPendingActions(actions);
            if (actions.some((a: any) => a.type === 'pause_conversation')) {
              setPaused(true);
              updateInitiatorConversationStatus('paused');
            }
          }
        },
        (messageId) => {
          addMessage({
            id: messageId || `agent-${Date.now()}`,
            request_id: requestId,
            stakeholder_id: null,
            role: 'agent',
            content: fullText,
            metadata_json: null,
            channel: 'web',
            target_stakeholder_id: null,
            sender_name: 'Discovery Agent',
            sender_email: null,
            created_at: new Date().toISOString(),
          });
          setStreaming(false);
          setStreamingText('');
        },
        (err) => {
          setError(err.message);
          setStreaming(false);
          setStreamingText('');
        },
      );
    },
    [requestId, isStreaming, addMessage, setStreaming, setStreamingText, appendStreamToken, setPendingActions, setError, updateInitiatorConversationStatus],
  );

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingText]);

  useEffect(() => {
    if (activeRequest) {
      const mySh = activeRequest.stakeholders.find((s) => s.role === 'initiator');
      if (mySh) {
        setEmailNotify(mySh.email_notifications);
        setPaused(mySh.conversation_status === 'paused');
      }
    }
  }, [activeRequest]);

  useEffect(() => {
    if (
      activeRequest &&
      activeRequest.description &&
      messages.length === 0 &&
      !isStreaming &&
      autoTriggeredRef.current !== activeRequest.id
    ) {
      autoTriggeredRef.current = activeRequest.id;
      sendToAgent(activeRequest.description, true);
    }
  }, [activeRequest, messages.length, isStreaming, sendToAgent]);

  useEffect(() => {
    if (!isStreaming) setAddingStakeholder(false);
  }, [isStreaming]);

  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.overflow = 'hidden';
    el.style.height = 'auto';
    const newHeight = Math.min(el.scrollHeight, 160);
    el.style.height = `${newHeight}px`;
    el.style.overflow = el.scrollHeight > 160 ? 'auto' : 'hidden';
  }, []);

  const handleSend = useCallback(async () => {
    if ((!input.trim() && pendingFiles.length === 0) || !requestId) return;

    const files = [...pendingFiles];
    const textParts: string[] = [];
    if (input.trim()) textParts.push(input.trim());

    setPendingFiles([]);
    setInput('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';

    if (files.length > 0) {
      setUploading(true);
      try {
        for (const f of files) {
          await discoveryApi.uploadAttachment(requestId, f);
        }
        const fileNames = files.map((f) => f.name).join(', ');
        textParts.push(`[Attached: ${fileNames}]`);
      } catch {
        setError('File upload failed');
        setUploading(false);
        return;
      }
      setUploading(false);
    }

    const content = textParts.join('\n\n');
    if (content) sendToAgent(content, true);
  }, [input, pendingFiles, requestId, sendToAgent, setError]);

  const handleStop = useCallback(() => {
    abortRef.current?.abort();
    setStreaming(false);
  }, [setStreaming]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  const handleStageFiles = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;
      setPendingFiles((prev) => [...prev, ...Array.from(files)]);
      setDragOver(false);
    },
    [],
  );

  const handleRemovePendingFile = useCallback((index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      handleStageFiles(e.dataTransfer.files);
    },
    [handleStageFiles],
  );

  const handleToggleNotify = useCallback(async () => {
    if (!activeRequest) return;
    const mySh = activeRequest.stakeholders.find((s) => s.role === 'initiator');
    if (!mySh) return;
    try {
      await discoveryApi.toggleNotifications(activeRequest.id, mySh.id, !emailNotify);
      setEmailNotify(!emailNotify);
    } catch {
      setError('Failed to toggle notifications');
    }
  }, [activeRequest, emailNotify, setError]);

  const handleActionAddStakeholder = useCallback(
    async (action: AgentAction) => {
      if (!requestId || !action.email) return;
      setAddingStakeholder(true);
      try {
        const { data } = await discoveryApi.addStakeholder(requestId, {
          email: action.email,
          name: action.name || undefined,
          context_note: action.context_note || '',
          language: action.language || undefined,
        });
        addStakeholder(data);
        setPendingActions(pendingActions.filter((a) =>
          !(a.type === 'suggest_stakeholder' && a.context_note === action.context_note)
        ));
        if (action.context_note) {
          discoveryApi.resolveAction(requestId, action.context_note).catch(() => {});
        }
        const stakeholderName = action.name || action.email;
        sendToAgent(
          `I've added ${stakeholderName} (${action.email}) as a stakeholder. They will be contacted separately in their own thread. Do NOT address them here — this chat is between you and me only. What should we focus on next?`,
          false,
        );
      } catch (err: any) {
        const is409 = err?.response?.status === 409;
        setError(is409 ? 'This person is already a stakeholder on this request.' : 'Failed to add stakeholder');
        setAddingStakeholder(false);
      }
    },
    [requestId, pendingActions, addStakeholder, setPendingActions, setError, sendToAgent, setAddingStakeholder],
  );

  const handleRemoveAttachment = useCallback(
    async (attachmentId: string, filename: string) => {
      if (!requestId) return;
      try {
        await discoveryApi.deleteAttachment(requestId, attachmentId);
        await loadRequest(requestId);
        sendToAgent(
          `I've removed the file "${filename}". Please clarify: should the information from this file be discarded, or should it remain part of what we've gathered so far?`,
          true,
        );
      } catch {
        setError('Failed to remove attachment');
      }
    },
    [requestId, loadRequest, sendToAgent, setError],
  );

  const handleActionDismiss = useCallback(
    (action: AgentAction) => {
      setPendingActions(pendingActions.filter((a) => a !== action));
      if (requestId && action.context_note) {
        discoveryApi.resolveAction(requestId, action.context_note).catch(() => {});
      }
    },
    [requestId, pendingActions, setPendingActions],
  );

  const handleGenerateSpec = useCallback(async () => {
    if (!requestId || generatingSpec) return;
    setGeneratingSpec(true);
    try {
      await discoveryApi.generateSpec(requestId);
      await loadRequest(requestId);
      setView('spec');
    } catch (err: any) {
      const detail = err?.response?.data?.detail || 'Failed to generate spec. Please try again.';
      toastError(detail);
      console.error('Generate spec failed:', err);
    } finally {
      setGeneratingSpec(false);
    }
  }, [requestId, generatingSpec, loadRequest, setView, toastError]);

  const existingEmails = React.useMemo(
    () => new Set((activeRequest?.stakeholders || []).map((s) => s.email.toLowerCase())),
    [activeRequest?.stakeholders],
  );
  const existingNames = React.useMemo(
    () => new Set((activeRequest?.stakeholders || []).filter((s) => s.name).map((s) => s.name!.toLowerCase())),
    [activeRequest?.stakeholders],
  );
  const resolvedContextNotes = React.useMemo(
    () => new Set<string>(activeRequest?.agent_context?.resolved_actions || []),
    [activeRequest?.agent_context],
  );

  if (!activeRequest) {
    return (
      <div className="flex items-center justify-center h-full text-content-muted text-sm">
        <p>Select a request from the board to start chatting.</p>
      </div>
    );
  }

  const badge = STATUS_BADGE[activeRequest.status] || STATUS_BADGE.draft;
  const attachments = activeRequest.attachments || [];

  return (
    <div className="flex h-full" ref={dropZoneRef} onDragOver={(e) => { if (e.dataTransfer.types.includes('Files')) { e.preventDefault(); setDragOver(true); } }} onDragLeave={(e) => { if (!dropZoneRef.current?.contains(e.relatedTarget as Node)) setDragOver(false); }} onDrop={handleDrop}>
      {/* Main chat area */}
      <div className="flex flex-col flex-1 min-w-0">
        {/* Chat header */}
        <div className="flex items-center gap-3 px-6 py-3 border-b border-subtle bg-surface-page">
          <button
            onClick={() => setView('board')}
            className="flex items-center justify-center w-8 h-8 rounded-lg text-content-muted hover:text-content hover:bg-surface-secondary transition-all"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-content truncate">
                {activeRequest.title || activeRequest.description || 'Untitled Request'}
              </h2>
              <span className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-semibold ${badge.bg} ${badge.text}`}>
                {badge.label}
              </span>
            </div>
          </div>
          <button
            onClick={handleToggleNotify}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
              emailNotify ? 'bg-brand/15 text-primary' : 'bg-surface-primary text-content-muted hover:text-content-secondary'
            }`}
            title={emailNotify ? 'Email notifications on' : 'Email notifications off'}
          >
            {emailNotify ? <Bell size={14} /> : <BellOff size={14} />}
            {emailNotify ? 'Notifications on' : 'Notifications off'}
          </button>
          <button
            onClick={() => setShowPanel(!showPanel)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
              showPanel ? 'bg-brand/15 text-primary' : 'bg-surface-primary text-content-muted hover:text-content-secondary'
            }`}
          >
            <UserPlus size={14} />
            Stakeholders
            {activeRequest.stakeholders.length > 0 && (
              <span className="ml-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-surface-secondary text-content-tertiary text-[10px] font-bold">
                {activeRequest.stakeholders.length}
              </span>
            )}
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="max-w-3xl mx-auto space-y-4">
            {messages.filter((msg) => msg.role !== 'system').map((msg) => (
              <MessageBubble
                key={msg.id}
                message={msg}
                existingEmails={existingEmails}
                existingNames={existingNames}
                resolvedContextNotes={resolvedContextNotes}
                onAddStakeholder={handleActionAddStakeholder}
                onDismissAction={handleActionDismiss}
                onGenerateSpec={handleGenerateSpec}
                generatingSpec={generatingSpec}
              />
            ))}

            {isStreaming && (
              <div className="flex gap-3 animate-fade-in-down">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-purple-500/15 shrink-0">
                  <Sparkles size={16} className="text-purple-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] text-purple-400 font-medium mb-1">Discovery Agent</div>
                  <div className="p-3 rounded-xl rounded-tl-sm bg-surface-primary border border-subtle">
                    {streamingText && (
                      <div
                        className="text-sm text-content-secondary leading-relaxed break-words"
                        dangerouslySetInnerHTML={{ __html: parseMarkdown(streamingText) }}
                      />
                    )}
                    <span className="inline-block w-0.5 h-4 bg-brand animate-pulse ml-0.5 align-middle" />
                  </div>
                </div>
              </div>
            )}

            {!isStreaming && filterCompletedActions(pendingActions, existingEmails, resolvedContextNotes).length > 0 && (
              <div className="space-y-3 pl-11">
                {filterCompletedActions(pendingActions, existingEmails, resolvedContextNotes).map((action, i) => (
                  <ActionCard
                    key={i}
                    action={action}
                    existingNames={existingNames}
                    onAddStakeholder={handleActionAddStakeholder}
                    onDismiss={handleActionDismiss}
                    onGenerateSpec={handleGenerateSpec}
                    generatingSpec={generatingSpec}
                  />
                ))}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Attachments bar */}
        {attachments.length > 0 && (
          <div className="px-6 py-2 border-t border-subtle bg-surface-page/80">
            <div className="max-w-3xl mx-auto flex items-center gap-2 overflow-x-auto">
              <Paperclip size={13} className="text-content-muted shrink-0" />
              {attachments.map((a) => (
                <AttachmentChip key={a.id} attachment={a} onRemove={handleRemoveAttachment} />
              ))}
            </div>
          </div>
        )}

        {/* Input area */}
        {paused ? (
          <div className="px-6 py-4 border-t border-subtle bg-surface-page">
            <div className="max-w-3xl mx-auto flex items-center justify-center">
              <button
                onClick={async () => {
                  if (requestId) {
                    try { await discoveryApi.resumeConversation(requestId); } catch { /* ignore */ }
                  }
                  setPaused(false);
                }}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand/15 text-primary text-sm font-medium hover:bg-brand/25 transition-all"
              >
                Add More Info
              </button>
            </div>
          </div>
        ) : (
        <div className="px-6 py-4 border-t border-subtle bg-surface-page">
          <div className="max-w-3xl mx-auto flex items-end gap-3">
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || addingStakeholder}
              className="flex items-center justify-center w-9 h-9 rounded-lg text-content-muted hover:text-content-secondary hover:bg-surface-secondary transition-all disabled:opacity-40 shrink-0"
              title="Attach file"
            >
              <Paperclip size={18} />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => { handleStageFiles(e.target.files); e.target.value = ''; }}
            />
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => { setInput(e.target.value); autoResize(); }}
              onKeyDown={handleKeyDown}
              placeholder="Describe your needs or answer the agent's questions..."
              rows={1}
              disabled={isStreaming || addingStakeholder}
              className="flex-1 px-4 py-2.5 rounded-xl bg-surface-primary border border-subtle text-content text-sm placeholder:text-content-faint resize-none overflow-hidden focus:outline-none focus:border-brand/40 focus:bg-surface-secondary transition-all disabled:opacity-40 min-h-[40px] max-h-[160px]"
            />
            {isStreaming ? (
              <button
                onClick={handleStop}
                className="flex items-center justify-center w-9 h-9 rounded-lg bg-status-error/15 text-status-error hover:bg-status-error/25 transition-all shrink-0"
                title="Stop generating"
              >
                <StopCircle size={18} />
              </button>
            ) : (
              <button
                onClick={handleSend}
                disabled={(!input.trim() && pendingFiles.length === 0) || uploading || addingStakeholder}
                className="flex items-center justify-center w-9 h-9 rounded-lg bg-brand text-content hover:bg-brand/80 disabled:opacity-30 disabled:cursor-not-allowed transition-all shrink-0"
              >
                {uploading ? (
                  <span className="w-4 h-4 border-2 border-content/30 border-t-content rounded-full animate-spin" />
                ) : (
                  <Send size={18} />
                )}
              </button>
            )}
          </div>
          {pendingFiles.length > 0 && (
            <div className="max-w-3xl mx-auto mt-2 flex flex-wrap gap-1.5">
              {pendingFiles.map((f, i) => (
                <div key={i} className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-surface-primary border border-subtle text-xs text-content-secondary">
                  <Paperclip size={11} className="text-content-muted shrink-0" />
                  <span className="truncate max-w-[150px]">{f.name}</span>
                  <button onClick={() => handleRemovePendingFile(i)} className="text-content-muted hover:text-content-secondary shrink-0"><X size={12} /></button>
                </div>
              ))}
            </div>
          )}
        </div>
        )}
      </div>

      {/* Stakeholder panel */}
      {showPanel && (
        <StakeholderPanel onClose={() => setShowPanel(false)} />
      )}

      {/* Drag overlay */}
      {dragOver && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-brand/5 border-2 border-dashed border-brand/40 rounded-xl pointer-events-none">
          <div className="flex flex-col items-center gap-2 text-primary">
            <Paperclip size={32} />
            <span className="text-sm font-medium">Drop files here</span>
          </div>
        </div>
      )}
    </div>
  );
};

/* ---- Helpers ---- */

function filterCompletedActions(
  actions: AgentAction[],
  existingEmails: Set<string>,
  resolvedContextNotes: Set<string>,
): AgentAction[] {
  return actions.filter((a) => {
    if (a.type !== 'suggest_stakeholder') return true;
    if (a.email && existingEmails.has(a.email.toLowerCase())) return false;
    if (a.context_note && resolvedContextNotes.has(a.context_note)) return false;
    return true;
  });
}

/* ---- Message Bubble ---- */

interface MessageBubbleProps {
  message: DiscoveryMessage;
  existingEmails: Set<string>;
  existingNames: Set<string>;
  resolvedContextNotes: Set<string>;
  onAddStakeholder: (action: AgentAction) => Promise<void>;
  onDismissAction: (action: AgentAction) => void;
  onGenerateSpec: () => void;
  generatingSpec?: boolean;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ message, existingEmails, existingNames, resolvedContextNotes, onAddStakeholder, onDismissAction, onGenerateSpec, generatingSpec }) => {
  const roleConf = ROLE_CONFIG[message.role] || ROLE_CONFIG.system;
  const Icon = roleConf.icon;
  const actions = filterCompletedActions(message.metadata_json?.actions || [], existingEmails, resolvedContextNotes);

  const isUser = message.role === 'user';

  return (
    <div className={`flex gap-3 animate-fade-in-down ${isUser ? 'flex-row-reverse' : ''}`}>
      <div className={`flex items-center justify-center w-8 h-8 rounded-full shrink-0 ${roleConf.color}`}>
        <Icon size={16} />
      </div>
      <div className={`flex-1 min-w-0 ${isUser ? 'items-end' : 'items-start'} flex flex-col`}>
        <div className={`flex items-center gap-2 mb-1 ${isUser ? 'flex-row-reverse' : ''}`}>
          <span className="text-[11px] font-medium text-content-secondary">{message.sender_name || roleConf.label}</span>
          <span className="text-[10px] text-content-faint">{formatMessageTime(message.created_at)}</span>
        </div>
        <div
          className={`p-3 text-sm leading-relaxed break-words max-w-[85%] ${
            isUser
              ? 'rounded-xl rounded-tr-sm bg-brand/10 text-content border border-brand/20'
              : message.role === 'system'
              ? 'rounded-xl rounded-tl-sm bg-surface-primary text-content-tertiary border border-subtle italic'
              : 'rounded-xl rounded-tl-sm bg-surface-primary text-content-secondary border border-subtle'
          }`}
          dangerouslySetInnerHTML={{ __html: parseMarkdown(message.content) }}
        />

        {actions.length > 0 && (
          <div className="mt-3 space-y-2 w-full">
            {actions.map((action, i) => (
              <ActionCard
                key={i}
                action={action}
                existingNames={existingNames}
                onAddStakeholder={onAddStakeholder}
                onDismiss={onDismissAction}
                onGenerateSpec={onGenerateSpec}
                generatingSpec={generatingSpec}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* ---- Stakeholder Suggestion Card ---- */

const StakeholderSuggestionCard: React.FC<{
  action: AgentAction;
  nameTaken: boolean;
  onAdd: (action: AgentAction) => Promise<void>;
  onDismiss: (action: AgentAction) => void;
}> = ({ action, nameTaken, onAdd, onDismiss }) => {
  const [confirmedNew, setConfirmedNew] = useState(!nameTaken);
  const [query, setQuery] = useState(action.email || '');
  const [results, setResults] = useState<UserLookupResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState(false);
  const [selected, setSelected] = useState<UserLookupResult | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [editName, setEditName] = useState(action.name || '');
  const [editContext, setEditContext] = useState(action.context_note || '');
  const [editLanguage, setEditLanguage] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    if (action.name && !action.email) {
      discoveryApi.userLookup(action.name).then(({ data }) => {
        if (data.length > 0) { setResults(data); setShowResults(true); }
      }).catch(() => {});
    }
  }, [action.name, action.email]);

  const handleSearch = useCallback((value: string) => {
    setQuery(value);
    setSelected(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.length < 2) { setResults([]); setShowResults(false); return; }
    setSearching(true);
    debounceRef.current = setTimeout(() => {
      discoveryApi.userLookup(value).then(({ data }) => {
        setResults(data);
        setShowResults(true);
      }).catch(() => {}).finally(() => setSearching(false));
    }, 300);
  }, []);

  const handleSelectUser = useCallback((user: UserLookupResult) => {
    setSelected(user);
    setQuery(user.email);
    setShowResults(false);
  }, []);

  const handleAdd = useCallback(async () => {
    const email = selected?.email || query.trim();
    if (!email || !email.includes('@') || adding) return;
    setAdding(true);
    try {
      await onAdd({
        ...action,
        email,
        name: editName.trim() || selected?.display_name || undefined,
        context_note: editContext.trim() || action.context_note || 'Stakeholder',
        language: editLanguage || undefined,
      });
    } finally {
      setAdding(false);
    }
  }, [action, selected, query, adding, editName, editContext, editLanguage, onAdd]);

  const isValidEmail = (selected?.email || query.trim()).includes('@');

  return (
    <div className="p-3 rounded-xl bg-blue-500/5 border border-blue-500/15">
      <div className="flex items-start gap-3">
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-500/15 shrink-0">
          <UserPlus size={16} className="text-blue-400" />
        </div>
        <div className="flex-1 min-w-0">
          <span className="text-xs font-semibold text-blue-400 mb-1.5 block">Suggested Stakeholder</span>

          {!confirmedNew && (
            <div className="mb-3 p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <p className="text-xs text-amber-300 leading-relaxed mb-2">
                <strong>{action.name}</strong> is already a stakeholder. Is this the same person or someone different?
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => onDismiss(action)}
                  className="px-3 py-1 rounded-lg bg-surface-primary text-content-muted text-xs font-medium hover:bg-surface-secondary transition-all"
                >
                  Same person
                </button>
                <button
                  onClick={() => setConfirmedNew(true)}
                  className="px-3 py-1 rounded-lg bg-blue-500/15 text-blue-400 text-xs font-medium hover:bg-blue-500/25 transition-all"
                >
                  Different person
                </button>
              </div>
            </div>
          )}

          {confirmedNew && <>
          <label className="block text-[10px] text-content-faint mb-0.5">Name</label>
          <input
            type="text"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            placeholder="Name"
            className="w-full bg-transparent text-sm text-content-secondary font-medium outline-none border-b border-dashed border-subtle/60 hover:border-content-muted focus:border-brand/40 focus:border-solid transition-colors pb-0.5"
          />
          <label className="block text-[10px] text-content-faint mt-2 mb-0.5">Context</label>
          <textarea
            value={editContext}
            onChange={(e) => setEditContext(e.target.value)}
            placeholder="Why this person is relevant..."
            rows={1}
            className="w-full bg-transparent text-xs text-content-tertiary leading-relaxed outline-none border-b border-dashed border-subtle/60 hover:border-content-muted focus:border-brand/40 focus:border-solid transition-colors resize-none"
          />
          <label className="flex items-center gap-1 text-[10px] text-content-faint mt-2 mb-0.5">
            <Globe size={9} /> Language
          </label>
          <div className="flex items-center gap-1">
            {LANGUAGE_OPTIONS.map((opt) => {
              const active = editLanguage === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setEditLanguage(opt.value)}
                  className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-medium transition-all ${
                    active
                      ? 'bg-blue-500/15 text-blue-400 border border-blue-500/30'
                      : 'bg-surface-primary border border-subtle text-content-muted hover:text-content hover:border-default'
                  }`}
                  title={opt.label}
                >
                  {opt.flag ? (
                    <span className={`fi fi-${opt.flag}`} />
                  ) : (
                    <Globe size={10} />
                  )}
                  {opt.label.split(' ')[0]}
                </button>
              );
            })}
          </div>
          </>}
        </div>
        <button
          onClick={() => onDismiss(action)}
          className="flex items-center justify-center w-6 h-6 rounded text-content-muted hover:text-content-secondary hover:bg-surface-secondary transition-all shrink-0"
        >
          <X size={14} />
        </button>
      </div>

      {confirmedNew && <div className="mt-3 ml-11 max-w-xs">
        {selected ? (
          <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-blue-500/10 border border-blue-500/20 text-xs">
            <CheckCircle size={13} className="text-blue-400 shrink-0" />
            <span className="text-content-secondary font-medium truncate">{selected.display_name || selected.email}</span>
            <span className="text-content-muted truncate">{selected.email}</span>
            <button onClick={() => { setSelected(null); setQuery(''); setShowResults(false); }} className="ml-auto text-content-muted hover:text-content-secondary shrink-0"><X size={12} /></button>
          </div>
        ) : (
          <div>
            <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-surface-primary border border-subtle focus-within:border-brand/40 transition-all">
              <Search size={13} className="text-content-muted shrink-0" />
              <input
                type="text"
                value={query}
                onChange={(e) => handleSearch(e.target.value)}
                onFocus={() => results.length > 0 && setShowResults(true)}
                placeholder="Search by name or enter email..."
                className="flex-1 bg-transparent text-xs text-content placeholder:text-content-faint outline-none min-w-0"
              />
              {searching && <span className="w-3 h-3 border border-brand/30 border-t-primary rounded-full animate-spin shrink-0" />}
            </div>
            {showResults && results.length > 0 && (
              <div className="mt-1.5 rounded-lg border border-default bg-surface-elevated overflow-hidden max-h-[160px] overflow-y-auto">
                {results.slice(0, 4).map((u) => (
                  <button
                    key={u.id}
                    onClick={() => handleSelectUser(u)}
                    className="w-full flex items-center gap-2 px-2.5 py-1.5 text-left hover:bg-surface-secondary transition-colors"
                  >
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-500/10 text-blue-400 text-[10px] font-bold shrink-0">
                      {(u.display_name || u.email)[0].toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <span className="text-xs text-content-secondary font-medium block truncate">{u.display_name || u.email}</span>
                      <span className="text-[10px] text-content-muted block truncate">{u.email}{u.org_name ? ` · ${u.org_name}` : ''}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <button
          onClick={handleAdd}
          disabled={!isValidEmail || adding}
          className="flex items-center gap-1.5 mt-2 px-3 py-1.5 rounded-lg bg-blue-500/15 text-blue-400 text-xs font-medium hover:bg-blue-500/25 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
        >
          {adding ? (
            <span className="w-3.5 h-3.5 border-2 border-blue-400/30 border-t-blue-400 rounded-full animate-spin" />
          ) : (
            <CheckCircle size={13} />
          )}
          {adding ? 'Adding...' : 'Add Stakeholder'}
        </button>
      </div>}
    </div>
  );
};

/* ---- Action Card ---- */

interface ActionCardProps {
  action: AgentAction;
  existingNames: Set<string>;
  onAddStakeholder: (action: AgentAction) => Promise<void>;
  onDismiss: (action: AgentAction) => void;
  onGenerateSpec: () => void;
  generatingSpec?: boolean;
}

const ActionCard: React.FC<ActionCardProps> = ({ action, existingNames, onAddStakeholder, onDismiss, onGenerateSpec, generatingSpec }) => {
  if (action.type === 'suggest_stakeholder') {
    return <StakeholderSuggestionCard action={action} nameTaken={!!(action.name && existingNames.has(action.name.toLowerCase()))} onAdd={onAddStakeholder} onDismiss={onDismiss} />;
  }

  if (action.type === 'request_stakeholder_followup') {
    return (
      <div className="flex items-start gap-3 p-3 rounded-xl bg-amber-500/5 border border-amber-500/15">
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-amber-500/15 shrink-0">
          <Mail size={16} className="text-amber-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold text-amber-400 mb-1">Follow-up Request</div>
          {action.name && <p className="text-sm text-content-secondary font-medium">{action.name}</p>}
          {action.questions && action.questions.length > 0 && (
            <ul className="mt-2 space-y-1">
              {action.questions.map((q, qi) => (
                <li key={qi} className="text-xs text-content-secondary leading-relaxed flex items-start gap-1.5">
                  <span className="text-amber-400 mt-0.5 shrink-0">•</span>
                  {q}
                </li>
              ))}
            </ul>
          )}
          <div className="flex items-center gap-2 mt-2">
            <button
              onClick={() => onDismiss(action)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500/15 text-amber-400 text-xs font-medium hover:bg-amber-500/25 transition-all"
            >
              <Send size={13} /> Send
            </button>
            <button
              onClick={() => onDismiss(action)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-surface-primary text-content-muted text-xs font-medium hover:text-content-secondary hover:bg-surface-secondary transition-all"
            >
              Skip
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (action.type === 'propose_spec') {
    return (
      <div className="flex items-start gap-3 p-3 rounded-xl bg-green-500/5 border border-green-500/15">
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-500/15 shrink-0">
          <FileText size={16} className="text-green-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold text-green-400 mb-1">Ready to Generate Spec</div>
          <p className="text-xs text-content-tertiary leading-relaxed">
            {action.reason || 'The agent has gathered enough information to generate a specification document.'}
          </p>
          <button
            onClick={onGenerateSpec}
            disabled={generatingSpec}
            className="flex items-center gap-1.5 mt-2 px-3 py-1.5 rounded-lg bg-green-500/15 text-green-400 text-xs font-medium hover:bg-green-500/25 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
          >
            {generatingSpec
              ? <><span className="w-3 h-3 border-2 border-green-400/30 border-t-green-400 rounded-full animate-spin" /> Generating…</>
              : <><FileText size={13} /> Generate Spec</>
            }
          </button>
        </div>
      </div>
    );
  }

  return null;
};

/* ---- Attachment Chip ---- */

const FILE_TYPE_ICON: Record<string, React.FC<{ size?: number; className?: string }>> = {
  image: Image,
  document: FileText,
  video: Film,
  audio: Volume2,
  other: File,
};

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const AttachmentChip: React.FC<{ attachment: Attachment; onRemove: (id: string, filename: string) => void }> = ({ attachment, onRemove }) => {
  const Icon = FILE_TYPE_ICON[attachment.file_type] || File;
  return (
    <div className="group flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-surface-primary border border-subtle text-xs text-content-secondary shrink-0 max-w-[220px]">
      <Icon size={13} className="shrink-0 text-content-muted" />
      <span className="truncate">{attachment.original_filename}</span>
      <span className="text-content-faint shrink-0">{formatFileSize(attachment.file_size)}</span>
      <button
        onClick={() => onRemove(attachment.id, attachment.original_filename)}
        className="shrink-0 text-content-muted hover:text-status-error opacity-0 group-hover:opacity-100 transition-all"
        title="Remove file"
      >
        <X size={12} />
      </button>
    </div>
  );
};

export default React.memo(DiscoveryChat);
